import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Key, AlertTriangle } from 'lucide-react';
import Logo from '../../components/Logo';
import { useToast } from '../../hooks/use-toast';

const PassphraseAccess = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [passphrase, setPassphrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);

  // Secret passphrase
  const SECRET_PASSPHRASE = 'EDGETRADE_SECURE_2024';

  // Check if already authenticated
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const passphraseAccess = localStorage.getItem('passphrase_access');
    
    if (token && passphraseAccess === 'true') {
      navigate('/admin-portal/dashboard');
    }
  }, [navigate]);

  const handlePassphraseAccess = async (e) => {
    e.preventDefault();

    if (locked) {
      toast({
        title: 'Access Locked',
        description: 'Too many failed attempts. Please try again later.',
        variant: 'destructive'
      });
      return;
    }

    if (!passphrase) {
      toast({
        title: 'Error',
        description: 'Please enter the security passphrase',
        variant: 'destructive'
      });
      return;
    }

    // Check passphrase
    if (passphrase !== SECRET_PASSPHRASE) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= 3) {
        setLocked(true);
        toast({
          title: 'Access Denied',
          description: 'Too many failed attempts. System locked.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Invalid Passphrase',
          description: `${3 - newAttempts} attempts remaining`,
          variant: 'destructive'
        });
      }
      setPassphrase('');
      return;
    }

    // Passphrase is correct - auto-login
    setLoading(true);

    try {
      const BACKEND_URL = '';
      
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: 'edgeadmin',
          password: 'EdgeAdmin2024!'
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store credentials
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('admin_user', JSON.stringify(data.admin));
        localStorage.setItem('passphrase_access', 'true');
        sessionStorage.setItem('security_clearance', 'granted');
        
        toast({
          title: 'Access Granted',
          description: 'Welcome to Edge Trade Admin Panel'
        });

        // Redirect to dashboard
        setTimeout(() => {
          navigate('/admin-portal/dashboard');
        }, 500);
      } else {
        toast({
          title: 'Error',
          description: 'System authentication failed. Please contact administrator.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Connection Error',
        description: 'Failed to connect to server',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-950 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Security Warning */}
        <div className="mb-6 p-4 bg-red-900/20 border-2 border-red-500/50 rounded-xl backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-6 h-6 text-red-400 animate-pulse" />
            <h2 className="text-red-400 font-bold text-lg">RESTRICTED AREA</h2>
          </div>
          <p className="text-red-300 text-xs">
            Unauthorized access is strictly prohibited. All attempts are logged.
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500/50 rounded-2xl p-8 shadow-2xl shadow-cyan-500/20">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo />
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-500 flex items-center justify-center">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-black text-cyan-400 mb-2">Admin Access</h1>
            <p className="text-cyan-600 text-sm">Enter Security Passphrase</p>
          </div>

          <form onSubmit={handlePassphraseAccess} className="space-y-6">
            {/* Passphrase Input */}
            <div>
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Security Passphrase
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-600" />
                <input
                  type="password"
                  name="passphrase"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="Enter passphrase"
                  disabled={locked || loading}
                  autoComplete="off"
                  className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400 transition-colors disabled:opacity-50 font-mono"
                />
              </div>
            </div>

            {/* Attempts Warning */}
            {attempts > 0 && !locked && (
              <div className="p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                <p className="text-orange-400 text-sm text-center">
                  ⚠️ Failed attempts: {attempts}/3
                </p>
              </div>
            )}

            {/* Locked Message */}
            {locked && (
              <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm text-center font-semibold">
                  🔒 ACCESS LOCKED - Too many failed attempts
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || locked || !passphrase}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-400 text-white font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/30"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Authenticating...
                </div>
              ) : (
                <>
                  <Shield className="w-5 h-5 inline mr-2" />
                  Access Admin Panel
                </>
              )}
            </button>
          </form>

          {/* Alternative Login Link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/admin-portal')}
              className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors"
            >
              Use username/password instead
            </button>
          </div>

          {/* Security Notice */}
          <div className="mt-6 p-3 bg-slate-900/50 border border-cyan-500/20 rounded-lg">
            <p className="text-cyan-600 text-xs text-center">
              🔐 Encrypted Connection | IP Logged | Activity Monitored
            </p>
          </div>
        </div>

        {/* System Info */}
        <div className="mt-6 text-center">
          <p className="text-slate-600 text-xs font-mono">
            System ID: ADM-{Math.random().toString(36).substring(2, 10).toUpperCase()}
          </p>
          <p className="text-slate-700 text-xs font-mono mt-1">
            Session: {new Date().toISOString().split('T')[0]}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PassphraseAccess;
